import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Star, Truck, Package, ShieldCheck, Calendar, Send } from "lucide-react";
import { toast } from "sonner";
import { VEHICLE_LABEL } from "../lib/pricing";

function StarRow({ score, size = 16 }) {
  const full = Math.floor(score);
  const hasHalf = score - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= full || (i === full + 1 && hasHalf);
        return (
          <Star
            key={i}
            width={size} height={size}
            className={filled ? "fill-[#1F4032] text-[#1F4032]" : "text-neutral-300"}
          />
        );
      })}
    </div>
  );
}

function memberDuration(createdAtIso) {
  if (!createdAtIso) return "—";
  const created = new Date(createdAtIso);
  const now = new Date();
  const months = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
  if (months < 1) {
    const days = Math.max(1, Math.round((now - created) / 86400000));
    return `${days} jour${days > 1 ? "s" : ""}`;
  }
  if (months < 12) return `${months} mois`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0 ? `${years} an${years > 1 ? "s" : ""} et ${remMonths} mois` : `${years} an${years > 1 ? "s" : ""}`;
}

export default function PublicProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/users/${id}/public`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 text-center font-mono text-sm">CHARGEMENT…</div>;
  if (!data) return <div className="p-10 text-center">Profil introuvable</div>;

  const { user, profile, ratings, completed_missions, published_missions, delivered_as_shipper } = data;
  const isValidated = user.kyc_status === "validated";

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="public-profile">
      <Link to="/" className="font-mono text-xs text-muted-foreground hover:text-[#1F4032]">← Retour</Link>

      <div className="mt-4 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="overline text-accent">— Profil</div>
            {isValidated && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> KYC VALIDÉ
              </span>
            )}
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">{user.name}</h1>
          {user.company_name && (
            <div className="text-muted-foreground mt-1">{user.company_name}</div>
          )}

          {/* Note moyenne */}
          <div className="flex items-center gap-4 mt-6 pb-6 border-b border-[#1F4032]/10">
            <StarRow score={ratings.average} size={28} />
            <div>
              <div className="font-display font-bold text-3xl tracking-tighter" data-testid="rating-average">
                {ratings.average.toFixed(1)} / 5
              </div>
              <div className="text-xs text-muted-foreground">basé sur {ratings.count} avis</div>
            </div>
          </div>

          {/* Avis */}
          <div className="mt-8">
            <h2 className="font-display font-bold text-xl mb-4">Avis reçus</h2>
            {ratings.items.length === 0 ? (
              <div className="border border-dashed border-[#1F4032]/20 p-6 text-center text-muted-foreground rounded-md">
                Vous n'avez pas encore reçu d'avis.<br />
                <span className="text-xs">Les notes seront affichées ici une fois que vous aurez livré ou reçu vos premières missions.</span>
              </div>
            ) : (
              <div className="space-y-3">
                {ratings.items.map((r, idx) => (
                  <div key={r.id || idx} className="border border-[#1F4032]/10 p-4 rounded-md" data-testid="rating-item">
                    <div className="flex items-center justify-between">
                      <StarRow score={r.score} />
                      <div className="font-mono text-xs text-muted-foreground">
                        {r.created_at?.slice(0, 10)}
                      </div>
                    </div>
                    {r.comment && <div className="text-sm mt-2">{r.comment}</div>}
                    {r.from_user_name && <div className="text-xs text-muted-foreground mt-1">— {r.from_user_name}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          {/* Activité globale */}
          <div className="border border-[#1F4032]/10 p-5 rounded-md bg-white">
            <div className="overline mb-3">Activité</div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-[#1F4032]" />
              <div>
                <div className="text-sm">Membre depuis</div>
                <div className="font-display font-bold">{memberDuration(user.created_at)}</div>
              </div>
            </div>
          </div>

          {/* Stats expéditeur */}
          {(published_missions > 0 || delivered_as_shipper > 0) && (
            <div className="border border-[#1F4032]/10 p-5 rounded-md bg-white">
              <div className="overline mb-3 flex items-center gap-1"><Send className="w-3 h-3" /> Expéditeur</div>
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-[#1F4032]" />
                <span className="font-display font-bold text-2xl">{published_missions}</span>
                <span className="text-sm text-muted-foreground">missions publiées</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span className="font-display font-bold text-2xl">{delivered_as_shipper}</span>
                <span className="text-sm text-muted-foreground">livrées</span>
              </div>
            </div>
          )}

          {/* Stats transporteur */}
          {profile && (
            <div className="border border-[#1F4032]/10 p-5 rounded-md bg-white">
              <div className="overline mb-3 flex items-center gap-1"><Truck className="w-3 h-3" /> Transporteur</div>
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-[#1F4032]" />
                <span className="font-display font-bold text-2xl">{completed_missions}</span>
                <span className="text-sm text-muted-foreground">missions livrées</span>
              </div>
              <div className="text-sm space-y-1 pt-3 border-t border-[#1F4032]/10">
                <div><span className="text-muted-foreground">Véhicule :</span> <b>{VEHICLE_LABEL[profile.vehicle_type] || profile.vehicle_type}</b></div>
                <div><span className="text-muted-foreground">Base :</span> {profile.base_city || "—"}</div>
                <div><span className="text-muted-foreground">Rayon :</span> {profile.radius_km} km</div>
                <div><span className="text-muted-foreground">Charge max :</span> {profile.max_weight_kg} kg</div>
              </div>
              {profile.bio && (
                <div className="text-sm mt-3 pt-3 border-t border-[#1F4032]/10 italic text-muted-foreground">
                  "{profile.bio}"
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
